import { useCallback, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import {
  type CloudAnalyticsModelAttributionGroupsModel,
  type Collaborators,
  type DashboardModelAttributionModel,
  type NotificationSettings,
  type PublicAccess,
  UserNotification,
  type UserPersonalNotificationModel,
} from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";
import { Typography } from "@mui/material";

import { useApiContext } from "../../../api/context";
import { attributionText } from "../../../assets/texts";
import { allocationTxt } from "../../../assets/texts/CloudAnalytics/allocation";
import ResourceEditDialog from "../../../Components/EditableResourceHeader/ResourceEditDialog";
import { useCloudAnalyticsAdminCheck } from "../../../Components/hooks/useCloudAnalyticsAdminCheck";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import {
  useErrorSnackbar,
  useSnackbar,
  useSuccessSnackbar,
} from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { ThreeDotsMenu, type ThreeDotsMenuOption } from "../../../Components/ThreeDotsMenu";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useUserContext } from "../../../Context/UserContext";
import { type Attribution } from "../../../types";
import { consoleErrorWithSentry } from "../../../utils";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { useHasAllocationWritePermission } from "../hooks/useHasAllocationWritePermission";
import {
  allocationsMenuOptions,
  type AttributionsMenuOption,
  AttributionsMenuOptionsValues,
  getNewCollaborators,
  isEditor,
  isOwner,
  isUserEditor,
} from "../utilities";
import { type AllocationRowItem, type AttributionDataWithId } from "./AllocationBrowser/AllocationBrowser";
import { renameAllocation, shareAllocation } from "./db";
import { trackAllocationBrowserEvent, trackBasicAllocationBrowserEvent } from "./utils";

export const useRenameAllocation = ({ row }) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const successSnackbar = useSuccessSnackbar();
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();

  const handleSave = useCallback(
    async (data: { name: string; description?: string }) => {
      try {
        renameAllocation(api, customer.id, row.ref.id, data);
        setIsRenameDialogOpen(false);
        successSnackbar(allocationTxt.ALLOCATION_NAME_UPDATE_SUCCESS);
      } catch (e) {
        errorSnackbar(allocationTxt.ALLOCATION_NAME_UPDATE_FAILED);
        consoleErrorWithSentry(e);
      }
    },
    [api, customer.id, errorSnackbar, row.ref.id, successSnackbar]
  );

  return {
    isRenameDialogOpen,
    openRenameDialog: () => {
      setIsRenameDialogOpen(true);
    },
    closeRenameDialog: () => {
      setIsRenameDialogOpen(false);
    },
    handleSave,
  };
};

export const useCreateAllocationHandler = ({
  baseUrl,
  newTab,
  prevPageUrl,
}: {
  baseUrl: string;
  prevPageUrl?: string;
  newTab?: boolean;
}) => {
  const history = useHistory();
  const routeMatchURL = useRouteMatchURL();
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });
  const { currentUser } = useAuthContext({ mustHaveUser: true });

  return useCallback(async () => {
    const nextPageUrl = `${baseUrl}/create`;

    trackBasicAllocationBrowserEvent({
      trackEvent,
      currentUserEmail: currentUser.email,
      eventName: "Allocation Create Initiated",
    });

    if (newTab) {
      window.open(nextPageUrl, "_blank");
    } else {
      history.push(nextPageUrl, { prevPage: prevPageUrl || routeMatchURL });
    }
  }, [baseUrl, newTab, history, prevPageUrl, routeMatchURL, currentUser.email, trackEvent]);
};

export const useAttributionSubscribeHandler = (
  updateUserEmailNotification: (selectedNotifications: NotificationSettings) => Promise<void>,
  notificationSettings?: NotificationSettings
) => {
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { userModel } = useUserContext({ allowNull: false });
  const { id } = userModel;
  const snackbar = useSnackbar();

  return useCallback(
    async ({
      attributionRef,
      isUserSubscribedToAttribution,
    }: {
      attributionRef: ModelReference<DashboardModelAttributionModel | CloudAnalyticsModelAttributionGroupsModel>;
      isUserSubscribedToAttribution: boolean;
    }) => {
      let dailyDigests = notificationSettings?.[UserNotification.DailyDigest]?.attributions || [];

      if (isUserSubscribedToAttribution) {
        dailyDigests = dailyDigests.filter((d) => d.id !== attributionRef.id);
      } else {
        dailyDigests = [...dailyDigests, attributionRef];
      }

      try {
        if (id && notificationSettings) {
          if (isDoitEmployee) {
            // a temporary hard-coded text, that should be removed sooner or later, this is why i don't extract it out to texts.js
            throw new Error("Doit employees cannot subscribe to daily digest at the moment");
          }
          if (dailyDigests.length === 0) {
            delete notificationSettings[UserNotification.DailyDigest];
          } else {
            notificationSettings[UserNotification.DailyDigest] = {
              attributions: dailyDigests,
            };
          }
          await updateUserEmailNotification(notificationSettings);
          snackbar.onOpen({
            message: isUserSubscribedToAttribution
              ? attributionText.SUCCESSFULLY_UNSUBSCRIBED
              : attributionText.SUCCESSFULLY_SUBSCRIBED,
            variant: "success",
            autoHideDuration: 5000,
          });
        }
      } catch (e: any) {
        snackbar.onOpen({
          message: e.message,
          variant: "error",
          autoHideDuration: 5000,
        });

        consoleErrorWithSentry(e);
      }
    },
    [snackbar, isDoitEmployee, notificationSettings, updateUserEmailNotification, id]
  );
};

export const useAllocationsThreeDotsMenu = ({
  row,
  handleShare,
  handleSettings,
  handleDelete,
  userEmailNotification,
  updateUserEmailNotification,
}: {
  row: AllocationRowItem;
  handleShare: () => void;
  handleSettings: () => void;
  handleDelete: (attributionId: string) => void;
  userEmailNotification?: UserPersonalNotificationModel;
  updateUserEmailNotification: (selectedNotifications: NotificationSettings) => Promise<void>;
}) => {
  const { currentUser, isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { userRoles } = useUserContext({ requiredRoles: true });
  const history = useHistory();
  const routeMatchURL = useRouteMatchURL();

  const isCloudAnalyticsAdmin = useCloudAnalyticsAdminCheck();
  const canModifyAllocations = useHasAllocationWritePermission();
  const displayShareMenu = useMemo(
    () => row.data.type === "custom" && (isUserEditor(currentUser.email, row.data) || isCloudAnalyticsAdmin),
    [currentUser.email, isCloudAnalyticsAdmin, row]
  );
  const attributionId = row.ref.id;
  const attributionRef = row.ref;
  const dailyDigestAttributions =
    userEmailNotification?.selectedNotifications?.[UserNotification.DailyDigest]?.attributions;
  const isUserSubscribedToAttribution = dailyDigestAttributions?.map((a) => a.id).includes(attributionId) ?? false;
  const menuOptions = useMemo(
    () =>
      allocationsMenuOptions
        .filter((option) => {
          switch (option.value) {
            case AttributionsMenuOptionsValues.ANOMALY_DETECTION:
              return (
                row.type === "Single" && row.data.type !== "preset" && (userRoles.anomaliesViewer || isDoitEmployee)
              );
            case AttributionsMenuOptionsValues.SUBSCRIBE:
              return !isUserSubscribedToAttribution && !isDoitEmployee;
            case AttributionsMenuOptionsValues.UNSUBSCRIBE:
              return isUserSubscribedToAttribution && !isDoitEmployee;
            case AttributionsMenuOptionsValues.DELETE:
              return isOwner(currentUser.email, { collaborators: row.data.collaborators } as Attribution);
            case AttributionsMenuOptionsValues.SHARE:
              return canModifyAllocations && displayShareMenu;
            case AttributionsMenuOptionsValues.RENAME:
              return (
                canModifyAllocations &&
                isEditor(currentUser.email, { collaborators: row.data.collaborators } as Attribution)
              );
            case AttributionsMenuOptionsValues.DUPLICATE:
              return canModifyAllocations;
            default:
              return true;
          }
        })
        .sort((b, a) => b.order - a.order),
    [
      row.type,
      row.data.type,
      row.data.collaborators,
      userRoles.anomaliesViewer,
      isDoitEmployee,
      isUserSubscribedToAttribution,
      currentUser.email,
      displayShareMenu,
      canModifyAllocations,
    ]
  );
  const subscribeAction = useAttributionSubscribeHandler(
    updateUserEmailNotification,
    userEmailNotification?.selectedNotifications
  );
  const { openRenameDialog, isRenameDialogOpen, closeRenameDialog, handleSave } = useRenameAllocation({ row });

  const handleSelection = useCallback(
    async ({ option }: { option: AttributionsMenuOption }) => {
      switch (option.value) {
        case AttributionsMenuOptionsValues.SUBSCRIBE:
        case AttributionsMenuOptionsValues.UNSUBSCRIBE:
          await subscribeAction({ attributionRef, isUserSubscribedToAttribution });
          break;

        case AttributionsMenuOptionsValues.SHARE:
          handleShare();
          break;

        case AttributionsMenuOptionsValues.DELETE:
          handleDelete(attributionId);
          break;

        case AttributionsMenuOptionsValues.ANOMALY_DETECTION:
          handleSettings();
          break;
        case AttributionsMenuOptionsValues.DUPLICATE:
          history.push({
            pathname: `${routeMatchURL}/create`,
            state: {
              allocationId: attributionId,
              type: "duplicate",
            },
          });
          break;
        case AttributionsMenuOptionsValues.RENAME:
          openRenameDialog();
          break;
      }
    },
    [
      subscribeAction,
      attributionRef,
      isUserSubscribedToAttribution,
      handleShare,
      handleDelete,
      attributionId,
      handleSettings,
      history,
      routeMatchURL,
      openRenameDialog,
    ]
  );

  const calculateOption = useCallback(
    (total, option) => [
      ...total,
      {
        label: <Typography color={option.secondary ? "error" : undefined}>{option.label}</Typography>,
        action: () =>
          handleSelection({
            option,
          }),
        key: option.value,
      },
    ],
    [handleSelection]
  );

  const options = useMemo<Array<ThreeDotsMenuOption>>(
    () => menuOptions.reduce(calculateOption, []),
    [calculateOption, menuOptions]
  );
  if (options.length === 0) {
    return;
  }

  return (
    <>
      <ThreeDotsMenu options={options} />
      {isRenameDialogOpen && (
        <ResourceEditDialog
          open={isRenameDialogOpen}
          onClose={closeRenameDialog}
          onSave={handleSave}
          hideDescription
          initialValues={{
            name: row.data.name,
            description: row.data.description,
          }}
          resourceName="allocation"
        />
      )}
    </>
  );
};

export const useShareAllocationHandler = (
  attributions: AttributionDataWithId[],
  closeDialog: () => void,
  setShareLoading: (loading: boolean) => void
) => {
  const errorSnackbar = useErrorSnackbar();
  const successSnackbar = useSuccessSnackbar();
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });
  const attributionsWithoutPresets = useMemo(() => attributions.filter((x) => x.type !== "preset"), [attributions]);
  return useCallback(
    async (collaborators: Collaborators, role: PublicAccess) => {
      setShareLoading(true);

      try {
        for (const attribution of attributionsWithoutPresets) {
          const newCollaborators = getNewCollaborators(collaborators, attribution.collaborators);
          await shareAllocation(
            api,
            customer.id,
            attribution.id,
            newCollaborators,
            role === "mixed" ? attribution.public : role
          );
          trackAllocationBrowserEvent({
            trackEvent,
            eventName: "Allocation Shared",
            allocationId: attribution.id,
            additionalProps: {
              role,
              collaborators: newCollaborators,
            },
          });
        }
        successSnackbar(attributionText.SHARE_SUCCESS);
      } catch (e) {
        errorSnackbar(attributionText.SHARE_FAILED);
      } finally {
        closeDialog();
      }

      setShareLoading(false);
    },
    [
      setShareLoading,
      successSnackbar,
      attributionsWithoutPresets,
      trackEvent,
      api,
      customer.id,
      errorSnackbar,
      closeDialog,
    ]
  );
};
